import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, ArrowUp, Clock } from 'lucide-react';
import useSiteSettings from '../utils/useSiteSettings';

const Footer = () => {
  const settings = useSiteSettings();
  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  return (
    <footer className="bg-orvix-navy border-t border-white/10 pt-16 pb-8">
      <div className="container-custom mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 mb-12">
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-gradient-to-br from-orvix-primary to-orvix-green rounded-xl flex items-center justify-center text-white font-bold overflow-hidden">{settings.logo ? <img src={settings.logo} alt={settings.companyName} className="w-full h-full object-cover" /> : 'O'}</div>
              <span className="text-xl font-bold text-white">{settings.companyName}</span>
            </div>
            <p className="text-gray-400 text-sm leading-relaxed mb-6">
              حلول برمجية مبتكرة لإدارة أعمالك بكفاءة عالية. نبني المستقبل الرقمي لشركتك.
            </p>
            <div className="flex gap-3">
              {['twitter', 'linkedin', 'github'].map((social) => (
                <a key={social} href="#" className="w-9 h-9 rounded-lg bg-white/5 flex items-center justify-center text-gray-400 hover:text-orvix-green hover:bg-orvix-green/10 transition-all">
                  <span className="text-xs capitalize">{social[0]}</span>
                </a>
              ))}
            </div>
          </div>

          <div>
            <h4 className="text-white font-bold mb-6">روابط سريعة</h4>
            <ul className="space-y-3">
              {[
                { path: '/', label: 'الرئيسية' },
                { path: '/services', label: 'الخدمات' },
                { path: '/portfolio', label: 'أعمالنا' },
                { path: '/contact', label: 'تواصل معنا' },
              ].map((link) => (
                <li key={link.path}>
                  <Link to={link.path} className="text-gray-400 hover:text-orvix-green transition-colors text-sm">{link.label}</Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-white font-bold mb-6">خدماتنا</h4>
            <ul className="space-y-3">
              {['أنظمة ERP', 'التطبيقات الجوالة', 'مواقع الويب', 'الاستشارات التقنية'].map((service) => (
                <li key={service}>
                  <span className="text-gray-400 text-sm">{service}</span>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-white font-bold mb-6">تواصل معنا</h4>
            <ul className="space-y-4">
              <li className="flex items-center gap-3 text-gray-400 text-sm">
                <Mail size={16} className="text-orvix-green" />
                {settings.email}
              </li>
              <li className="flex items-center gap-3 text-gray-400 text-sm">
                <Phone size={16} className="text-orvix-green" />
                {settings.phone}
              </li>
              <li className="flex items-start gap-3 text-gray-400 text-sm">
                <MapPin size={16} className="text-orvix-green mt-0.5" />
                {settings.address}
              </li>
              <li className="flex items-start gap-3 text-gray-400 text-sm">
                <Clock size={16} className="text-orvix-green mt-0.5" />
                <span>{settings.workingDays}<br />{settings.workingHours}</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row items-center justify-between gap-4 relative">
          <div className="flex items-center gap-3">
            <Link to="/admin/login" className="text-gray-600 hover:text-orvix-primary text-sm transition-colors" title="لوحة الإدارة">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
            </Link>
            <p className="text-gray-500 text-sm">© 2026 {settings.companyName}. جميع الحقوق محفوظة.</p>
          </div>
          <button onClick={scrollToTop} className="w-10 h-10 rounded-lg bg-orvix-primary/20 text-orvix-primary hover:bg-orvix-primary hover:text-white transition-all flex items-center justify-center">
            <ArrowUp size={18} />
          </button>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
